import { Tag, Search, Settings, Trash, Plus, X, AlertCircle, CheckCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CategoryForm from '../components/CategoryForm';
import {
  fetchCategories,
  deleteCategory,
  clearError,
  clearSuccess,
  selectCategories,
  selectCategoriesLoading,
  selectCategoriesError,
  selectCategoriesSuccess,
  selectCategoriesMessage,
  selectCategoriesDeleting
} from '../redux/CategorySlice';

const Categories = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const categories = useSelector(selectCategories);
  const isLoading = useSelector(selectCategoriesLoading);
  const isDeleting = useSelector(selectCategoriesDeleting);
  const error = useSelector(selectCategoriesError);
  const success = useSelector(selectCategoriesSuccess);
  const message = useSelector(selectCategoriesMessage);

  // Local state
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const statuses = ['Active', 'Inactive'];

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Auto-dismiss success/error messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (categoryToDelete) {
      try {
        await dispatch(deleteCategory(categoryToDelete.categoryid)).unwrap();
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
    setShowDeleteConfirm(false);
    setCategoryToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setCategoryToDelete(null);
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const filteredCategories = categories?.filter(category => {
    const matchesSearch = category?.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || category.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTotalProductCount = () => {
    return categories?.reduce((total, category) => total + (category.no_products || 0), 0);
  };

  const getActiveCount = () => {
    return categories?.filter(c => c.status === 'Active').length;
  };

  if (isLoading && categories?.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-amber-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Success/Error Notification */}
      {(success || error) && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
          success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center space-x-3">
            {success ? (
              <CheckCircle className="text-green-600" size={20} />
            ) : (
              <AlertCircle className="text-red-600" size={20} />
            )}
            <span className={success ? 'text-green-800' : 'text-red-800'}>
              {success ? message : error}
            </span>
          </div>
          <button
            onClick={() => {
              dispatch(clearSuccess());
              dispatch(clearError());
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Categories</h1>
          <p className="text-gray-500 text-sm">
            Manage your product categories • {categories?.length} categories • {getTotalProductCount()} total products
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Category</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Categories</p>
              <p className="text-2xl font-bold text-gray-900">{categories?.length}</p>
            </div>
            <div className="bg-amber-100 p-3 rounded-lg">
              <Tag className="text-amber-600" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Active Categories</p>
              <p className="text-2xl font-bold text-gray-900">{getActiveCount()}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <Tag className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{getTotalProductCount()}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Tag className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent w-full"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            All Categories ({filteredCategories?.length})
          </h3>

          {filteredCategories?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCategories.map((category) => (
                <div key={category.categoryid} className="group relative bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200">
                  {/* Category Image */}
                  <div className="aspect-video w-full bg-gray-200 overflow-hidden">
                    {category.image ? (
                      <img
                        src={category.image}
                        alt={category.category}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full flex items-center justify-center" style={{ display: category.image ? 'none' : 'flex' }}>
                      <Tag size={40} className="text-gray-400" />
                    </div>
                  </div>

                  {/* Category Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-lg truncate pr-2">
                        {category.category}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(category.status)}`}>
                        {category.status}
                      </span>
                    </div>
                    
                    <div className="space-y-1 mb-4">
                      <p className="text-sm text-gray-600">
                        {category.no_products || 0} products
                      </p>
                      <p className="text-xs text-gray-500">
                        Created: {category.createdAt ? new Date(category.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Category"
                          disabled={isDeleting}
                        >
                          <Settings size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(category)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete Category"
                          disabled={isDeleting}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                        disabled={isDeleting}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Tag size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {categories?.length === 0 ? 'No categories yet' : 'No categories found'}
              </h3>
              <p className="text-gray-500 mb-4">
                {categories?.length === 0 
                  ? 'Create your first category to get started' 
                  : 'Try adjusting your search or filters'
                }
              </p>
              <button
                onClick={handleAddNew}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
              >
                {categories?.length === 0 ? 'Add Your First Category' : 'Add New Category'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Category</h3>
              </div>
            </div>

            <p className="text-gray-600 mb-2">
              Are you sure you want to delete the category <strong>"{categoryToDelete?.category}"</strong>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This action cannot be undone. All products in this category will need to be reassigned.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelDelete}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <CategoryForm
            setShowModal={setShowModal}
            editingCategory={editingCategory}
            setEditingCategory={setEditingCategory}
          />
        </div>
      )}
    </div>
  );
};

export default Categories;